import { useMemo } from "react";
import { usePermissions, useRecordContext, type RaRecord } from "react-admin";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useItsOneViewForIts } from "@/hooks/useItsOneView";
import { hasPermission } from "@/utils/permission-utils";
import { formatInr } from "@/utils/format-inr";
import {
  buildItsOneViewCommitmentSummaries,
  type ItsOneViewCommitmentSummary,
} from "@/utils/its-one-view-commitment-summaries";

function MetricCell({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: color ?? "text.primary",
          mt: 0.25,
        }}
        noWrap
      >
        {value}
      </Typography>
    </Box>
  );
}

function ModuleStatCard({ row }: { row: ItsOneViewCommitmentSummary }) {
  const hasDue = row.balance > 0;
  const paidPct = row.total > 0 ? Math.min(100, Math.round((row.paid / row.total) * 100)) : 0;
  const balanceColor = hasDue ? "warning.main" : "success.main";

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
        {row.label}
      </Typography>

      <Box sx={{ my: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          Balance due
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: balanceColor,
            lineHeight: 1.2,
          }}
        >
          {formatInr(row.balance)}
        </Typography>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {paidPct}% paid
          </Typography>
          <Typography variant="caption" color="text.secondary">
            of {formatInr(row.total)}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={paidPct}
          color={hasDue ? "warning" : "success"}
          sx={{ height: 6, borderRadius: 1 }}
        />
      </Box>

      <Divider sx={{ mt: "auto", mb: 1.5 }} />

      <Stack direction="row" spacing={1} divider={<Divider orientation="vertical" flexItem />}>
        <MetricCell label="Total" value={formatInr(row.total)} />
        <MetricCell label="Paid" value={formatInr(row.paid)} color="success.main" />
      </Stack>
    </Paper>
  );
}

const RazaRequestCommitmentsPanel = () => {
  const record = useRecordContext<RaRecord & { itsNo?: string }>();
  const { permissions } = usePermissions();
  const canView = hasPermission(permissions, "itsOneView.view");
  const recordIts = typeof record?.itsNo === "string" ? record.itsNo : "";

  const { data, loading, error, itsValid } = useItsOneViewForIts(recordIts, {
    persistLastIts: false,
    includeSettled: true,
    enabled: canView,
  });

  const modules = useMemo(() => (data ? buildItsOneViewCommitmentSummaries(data) : null), [data]);

  if (!record) return null;

  if (!canView) {
    return (
      <Box sx={{ maxWidth: 720, py: 1 }}>
        <Alert severity="info">
          You do not have permission to view ITS One View for this member.
        </Alert>
      </Box>
    );
  }

  if (!itsValid) {
    return (
      <Box sx={{ maxWidth: 720, py: 1 }}>
        <Alert severity="warning">This request does not have a valid 8-digit ITS number.</Alert>
      </Box>
    );
  }

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 720, py: 1 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data || !modules) {
    return null;
  }

  if (modules.length === 0) {
    return (
      <Box sx={{ maxWidth: 720, py: 1 }}>
        <Alert severity="info">No commitments found for this household.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, width: "100%", py: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {data.hofName ?? "Household"} · HOF {data.hofIts}
        {data.lookupIts !== data.hofIts ? ` · ITS ${data.lookupIts}` : ""}
      </Typography>

      <Grid container spacing={2}>
        {modules.map((row) => (
          <Grid key={row.key} size={{ xs: 12, sm: 4, md: 3 }}>
            <ModuleStatCard row={row} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default RazaRequestCommitmentsPanel;
